import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { SellerListings } from "@/vault/marketplace/SellerListings";
import { useFeature } from "@/context/SiteModeContext";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { Crumbs, readyPills, useKeepStore } from "@/components/layout/Crumbs";
import { READY_BY_KEY, READY_IN } from "@/lib/nav";
import { LandingHead } from "@/components/shop/GroupCard";
import { Fold } from "@/components/layout/Fold";
import { PageBanner } from "@/components/mall/PageBanner";

/** One material. The picture is the card; the details sit under it in plain type. */
function MaterialCard({ c, theme, ready, keep }) {
    return (
        <Link to={keep(`/signs/${c.slug}${ready ? `?ready=${ready}` : ""}`)} data-testid={`sign-cat-${c.slug}`}
            className="group flex flex-col border-2 border-zinc-900 bg-white transition-transform duration-200 hover:-translate-y-1">
            <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-zinc-900 bg-zinc-200">
                <img src={mediaUrl(c.image_url)} alt={c.name} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                <span className="absolute left-0 top-0 bg-white px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-zinc-900" data-testid={`sign-cat-count-${c.slug}`}>
                    {c.product_count} product{c.product_count === 1 ? "" : "s"}
                </span>
            </div>
            <div className="flex flex-1 items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                    <h3 className="font-display text-base font-black uppercase leading-tight tracking-tight text-zinc-900">{c.name}</h3>
                    {c.description && <p className="mt-0.5 line-clamp-2 text-sm text-zinc-600">{c.description}</p>}
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center transition-transform duration-200 group-hover:translate-x-1" style={{ background: theme.primary, color: theme.on || "#fff" }}>
                    <ArrowRight size={16} />
                </span>
            </div>
        </Link>
    );
}

/** SIGNS landing (Phase 10): one card per material this shop makes → product cards → the full-screen designer. */
export default function Signs() {
    const listingsOn = useFeature("listings");
    const [cats, setCats] = useState(null);
    const [meta, setMeta] = useState({});
    const [params] = useSearchParams();
    const ready = READY_BY_KEY[params.get("ready")] ? params.get("ready") : null;
    const { theme: t, store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const keep = useKeepStore();

    useEffect(() => {
        if (!shopSlug) return;
        api.get("/signs/categories", { params: { store: shopSlug, ready: ready || undefined } })
            .then(({ data }) => { setCats(data.categories || []); setMeta(data); }).catch(() => setCats([]));
    }, [shopSlug, ready]);

    return (
        <Layout title="Signs">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="signs-page">
                <Reveal>
                    <Crumbs trail={[{ label: "Signs", to: "/signs" }, ...(ready ? [{ label: READY_BY_KEY[ready].label }] : [])]}
                        pills={readyPills("/signs", meta.counts || {}, ready, READY_IN)} />
                    <div className="mt-4">
                        <LandingHead testid="signs-head"
                            kicker={<span data-testid="signs-kicker">{ready ? `Signs ready in ${READY_BY_KEY[ready].label.toLowerCase()}` : "Most signs are ready in 3 to 5 business days"}</span>}
                            title={<span data-testid="signs-title">Pick a material.</span>}
                            line={<span data-testid="signs-store-note">{activeStore ? <>Every sign here is made by <span className="font-semibold text-zinc-900">{activeStore.name}</span>. </> : null}Pick the kind of sign, then the product — you set the size and drop your art on it, and the price updates live.</span>} />
                    </div>
                </Reveal>

                {cats === null ? (
                    <div className="flex min-h-[40vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
                ) : cats.length === 0 ? (
                    <p className="mt-8 text-zinc-600" data-testid="signs-empty">This shop has no signs on the shelf yet.</p>
                ) : (
                    <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="sign-materials">
                        {cats.map((c, i) => (
                            <Reveal key={c.slug} delay={i * 0.05}><MaterialCard c={c} theme={t} ready={ready} keep={keep} /></Reveal>
                        ))}
                    </div>
                )}
            </section>

            <Fold title="How sign pricing works — materials, finishing, turnaround" kicker="The fine print" testid="signs-fold-about">
                <PageBanner room="signs" />
            </Fold>

            {listingsOn && <SellerListings kind="supply" heading="Sign gear and supplies from other shops"
                blurb="Classified ads posted by other shops. You deal with the seller directly." sellCta="Sell your material" testid="seller-signs" />}
        </Layout>
    );
}
